import { IWithI18nProps, withI18n } from '../../components/context/i18n';
import withContextConsumer from '../../components/context/withContextConsumer';
import React, { createContext, useContext, useCallback, useState } from 'react';

interface PageAnnouncementContextValue {
  announceNav: (pageTitle: string) => void;
  announceSearch: (searchTerm: string, numResults: number) => void;
  announceLoadMore: (count: number) => void;
}

const PageAnnouncementContext =
  createContext<PageAnnouncementContextValue | null>(null);

interface PageAnnouncementProviderProps extends IWithI18nProps {
  children: React.ReactNode;
}

const PageAnnouncementProviderInternal: React.FC<
  PageAnnouncementProviderProps
> = ({ children, t }) => {
  const [announcementMessage, setAnnouncementMessage] = useState<
    string | undefined
  >();
  const announce = useCallback((message: string) => {
    // Clear first, then set to ensure announcement ??need it
    setAnnouncementMessage(undefined);
    setTimeout(() => {
      setAnnouncementMessage(message);
    }, 10);
  }, []);

  const announceNav = useCallback(
    (pageTitle: string) => {
      announce(t('pages.announcements.navigation', { title: pageTitle }));
    },
    [announce, t],
  );

  const announceSearch = useCallback(
    (searchTerm: string, resultsNum: number) => {
      announce(
        t('pages.main.search.results-found', {
          count: resultsNum,
          term: searchTerm,
        }),
      );
    },
    [announce, t],
  );
  const announceLoadMore = useCallback(
    (count: number) => {
      announce(t('pages.main.episodes.loaded-more', { count }));
    },
    [announce, t],
  );

  const contextValue: PageAnnouncementContextValue = {
    announceNav,
    announceSearch,
    announceLoadMore,
  };

  return (
    <PageAnnouncementContext.Provider value={contextValue}>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcementMessage}
      </div>
      {children}
    </PageAnnouncementContext.Provider>
  );
};
export const PageAnnouncementProvider = withI18n(
  PageAnnouncementProviderInternal,
);

export const usePageAnnouncement = (): PageAnnouncementContextValue => {
  const context = useContext(PageAnnouncementContext);
  if (!context) {
    throw new Error(
      'usePageAnnouncement must be used within PageAnnouncementProvider',
    );
  }
  return context;
};

export interface IWithPageAnnouncementProps {
  pageAnnouncers: PageAnnouncementContextValue;
}

export const withPageAnnouncement = withContextConsumer(
  PageAnnouncementContext.Consumer,
  (pageAnnouncers) => ({ pageAnnouncers }),
);
